import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { Box, Grid, TextField, Typography } from "@mui/material"
import {
  getCheckoutNoteDisplayValue,
  getDeliveryScheduleFromCheckoutNote,
} from "src/app/utils"
import useUpdateEmailAndShippingAddress from "src/app/hooks/useUpdateEmailAndShippingAddress"
import { useUpdateCheckoutAttributesMutation } from "src/features/checkout/storefrontApi"
import useUpdateShippingLine from "src/app/hooks/useUpdateShippingLine"
import BackAndNextButton from "../common/BackAndNextButton"
import EmailInput from "../common/EmailInput"
import PhoneInput from "../common/PhoneInput"
import TextInput from "../common/TextInput"
import TextareaWithCharCount from "../common/CharacterCountTextarea"
import InformationHeader from "./InformationHeader"
import analytics from "src/app/analytics"
import { useCartNoteUpdateMutation } from "src/features/checkout/storefrontCartApi"

function BottomNav({ loading }) {
  return (
    <BackAndNextButton
      loading={loading}
      // backButtonLabel="Return to schedule"
      backButtonPath="../schedule"
      nextButtonLabel="Continue to payment"
      nextButtonType="submit"
    />
  )
}

export default function PickupForm() {
  const navigate = useNavigate()
  const customer = useSelector((state) => state.checkout.customer)
  const cart = useSelector((state) => state.checkout.cart)
  const pickupLocation = useSelector(
    (state) => state.checkout.options.pickupLocation,
  )

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      email: customer.email || cart.buyerIdentity?.email || "",
      company: cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.company || "",
      firstName: cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.firstName || "",
      lastName: cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.lastName || "",
      address1: pickupLocation.address1 || "",
      address2: pickupLocation.address2 || "",
      city: pickupLocation.city || "",
      country: "United States",
      province: "Hawaii",
      phone: cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.phone || "",
      zip: pickupLocation.zip || "",
      note: getCheckoutNoteDisplayValue(cart.note || ""),
    },
  })

  const [
    updateEmailAndShippingAddress,
    { isLoading: updateEmailAndShippingAddressLoading },
  ] = useUpdateEmailAndShippingAddress()

  const [
    cartNoteUpdate,
    {
      data: cartNoteUpdateData,
      isLoading: cartNoteUpdateLoading,
    },
  ] = useCartNoteUpdateMutation()

  const [
    updateCheckoutAttributes,
    { isLoading: updateCheckoutAttributesLoading },
  ] = useUpdateCheckoutAttributesMutation()

  const [updateShippingLine, { isLoading: updateShippingLineLoading }] =
    useUpdateShippingLine()

  const onSubmit = ({ email, note, ...shippingAddress }) => {
    const { firstName, lastName, ...shipping_address } = shippingAddress

    analytics.setUserProperties({
      email,
    })

    analytics.track("add_pickup_info", {
      cart_id: cart.id,
      email,
      first_name: firstName,
      last_name: lastName,
      ...shipping_address,
    })

    updateEmailAndShippingAddress({ email, shippingAddress }).finally(async () => {
      const newCheckoutNote = [
        getDeliveryScheduleFromCheckoutNote(cart.note || ""),
        note,
      ]
        .filter(Boolean)
        .join(" ")

      if (cart.note !== newCheckoutNote) {
        analytics.track("add_note", {
          cart_id: cart.id,
          note: newCheckoutNote,
        })
        await cartNoteUpdate({
          cartId: cart.id,
          note: newCheckoutNote,
        })
        // .finally(() =>
        //   updateShippingLine().finally(() => navigate("../payment")),
        // )
        navigate("../payment")
      } else {
        navigate("../payment")
        // updateShippingLine().finally(() => navigate("../payment"))
      }
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box>
        <InformationHeader />

        {!customer?.id && (
          <>
            <Typography variant="h6">Who's Buying</Typography>
            <Typography color="text.secondary" variant="subtitle1">
              (Buyers Information)
            </Typography>
            <EmailInput
              name="email"
              control={control}
              label="Your Email Address"
              requiredErrorMessage="Please enter your email address."
              size="small"
              fullWidth
              sx={{ my: 2 }}
            />
          </>
        )}

        <Typography variant="h6">Who's picking it up</Typography>
        <Typography color="text.secondary" variant="subtitle1">
          (Pickup Information)
        </Typography>

        <Grid container spacing={2} mt={0} mb={2}>
          <Grid item xs={12} sm={6}>
            <TextInput
              name="firstName"
              control={control}
              rules={{
                required: {
                  message:
                    "Please enter the first name of the person who'll pick it up.",
                  value: true,
                },
              }}
              label="First name of the person who'll pick it up"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              name="lastName"
              control={control}
              rules={{
                required: {
                  message:
                    "Please enter last name of the person who'll pick it up.",
                  value: true,
                },
              }}
              label="Last name of the person who'll pick it up"
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>

        <TextField
          defaultValue={pickupLocation.fixed_address}
          label="Pickup Address"
          size="small"
          disabled
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            readOnly: true,
          }}
        />

        <PhoneInput
          control={control}
          label="Phone # of the person who'll pick it up"
          requiredErrorMessage="Please enter the phone # of the person who'll pick it up."
          size="small"
          fullWidth
        />

        <TextareaWithCharCount
          name="note"
          control={control}
          label="Personalized Gift Message and/or Special Instructions"
          size="small"
          rows={4}
          multiline
          fullWidth
          maxLength={150}
          value={watch("note")}
          sx={{ my: 2 }}
        />

        <BottomNav
          loading={
            updateEmailAndShippingAddressLoading ||
            updateCheckoutAttributesLoading ||
            updateShippingLineLoading
          }
        />
      </Box>
    </form>
  )
}
